import { cloneElement } from "react"
import { Field, ErrorMessage, FormikValues } from "formik"
import Label from "./label"
import If from "../if"

type Props = {
  label: string
  subLabel?: string
  name: string
  type?: HTMLInputElement["type"]
  as?: string
  className?: string
  /** Overrides default input element classes when set. When `beforeIcon` is set, `pl-10` is still appended. */
  inputFieldClassName?: string
  /** Overrides the default `mt-1 relative flex rounded-md shadow-sm` wrapper around the field. */
  innerWrapperClassName?: string
  /** Appended to Label `className` (e.g. `text-sm text-gray-600`). */
  labelClassName?: string
  helpText?: string
  tooltipText?: string
  autoComplete?: string
  placeholder?: string
  beforeLabel?: string | JSX.Element
  beforeIcon?: JSX.Element
  afterIcon?: string | JSX.Element
  inputProps?: { [key: string]: any }
  showLabel?: boolean
  required?: boolean
  disabled?: boolean
  children?: any
  renderError?: (msg: string) => React.ReactNode
}

const Input = ({
  showLabel = true,
  label,
  subLabel,
  name,
  type = "text",
  as,
  helpText,
  tooltipText,
  autoComplete,
  placeholder,
  beforeLabel,
  beforeIcon,
  afterIcon,
  required = false,
  className = "",
  inputFieldClassName,
  innerWrapperClassName,
  labelClassName,
  inputProps = {},
  disabled,
  children,
  renderError
}: Props) => {
  const disabledClassName = disabled ? "text-neutral-400" : ""
  const innerWrap =
    innerWrapperClassName ?? "mt-1 relative flex rounded-md shadow-sm"

  const afterIconPad = afterIcon ? "pr-12" : ""

  let fieldClassName: string
  if (inputFieldClassName !== undefined) {
    fieldClassName = [
      inputFieldClassName,
      beforeIcon ? "pl-10" : "",
      afterIconPad
    ]
      .filter(Boolean)
      .join(" ")
  } else {
    fieldClassName = `appearance-none block w-full px-3 py-2.5 border border-neutral-200 ${
      beforeLabel ? "rounded-none rounded-r-md" : "rounded-md"
    } ${
      beforeIcon ? "pl-10" : ""
    } ${afterIconPad} placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${disabledClassName}`
  }

  return (
    <div className={className}>
      <Label
        className={[disabledClassName, labelClassName]
          .filter(Boolean)
          .join(" ")}
        name={name}
        showLabel={showLabel}
        label={label}
        subLabel={subLabel}
        required={required}
        helperText={tooltipText}
      />
      <div className={innerWrap}>
        {beforeLabel && (
          <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
            {beforeLabel}
          </span>
        )}
        {beforeIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 py-2 flex items-center pointer-events-none">
            {beforeIcon}
          </div>
        )}
        <Field
          id={name}
          name={name}
          type={type}
          autoComplete={autoComplete}
          placeholder={placeholder}
          as={as}
          className={fieldClassName}
          {...inputProps}
          disabled={disabled}
        >
          {children
            ? (props: { field: FormikValues; meta: any; form: any }) =>
                cloneElement(children, { ...props, className: fieldClassName })
            : null}
        </Field>
        {afterIcon ? (
          <div className="absolute inset-y-0 right-0 z-10 flex items-center pr-2.5">
            {afterIcon}
          </div>
        ) : null}
      </div>

      <If
        condition={!!helpText}
        then={<p className="mt-2 text-sm text-neutral-400">{helpText}</p>}
      />

      {renderError ? (
        <ErrorMessage name={name}>
          {msg => (
            <p className="mt-2 text-sm text-red-600">{renderError(msg)}</p>
          )}
        </ErrorMessage>
      ) : (
        <ErrorMessage
          name={name}
          component="p"
          className="mt-2 text-sm text-red-600"
        />
      )}
    </div>
  )
}

export default Input
